import React from 'react';
import { RiSearch2Line, RiArrowRightLine } from 'react-icons/ri';
import * as yup from 'yup';
import { useFormik } from 'formik';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';

import { MESSAGES } from '../../../../../../core/constants/forms.constants';
import { FormInput, FormSelect, SelectOption } from '@omnigenbiodata/ui';
import { Freezer } from '../../../../../../core/api/lab.types';

interface StorageSearchValues {
  searchTerm: string;
  freezerId: string;
  bioBanked: string;
}

interface StorageSearchProps {
  onSearch: (values: StorageSearchValues) => void;
  searchTerm?: string;
  freezerId?: string;
  bioBanked?: string;
  buttonLabel?: string;
  freezerList?: Freezer[];
  stack?: boolean;
}

function StorageSearch({
  searchTerm = '',
  freezerId = '',
  bioBanked = 'false',
  buttonLabel = 'Search',
  onSearch,
  freezerList = [],
  stack = false,
}: StorageSearchProps) {
  const validationSchema = yup.object({
    searchTerm: yup.string(),
    freezerId: yup.string(),
    bioBanked: yup.string().required(MESSAGES.biobankedRequired),
  });

  const formik = useFormik<StorageSearchValues>({
    enableReinitialize: true,
    initialValues: {
      searchTerm,
      freezerId,
      bioBanked,
    },
    validateOnMount: true,
    validationSchema,
    onSubmit: (values) => {
      onSearch(values);
    },
  });

  return (
    <Box data-testid="cryoboxSearch">
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={stack ? 12 : 4}>
            <FormInput
              label="Cryobox ID / Aliquot ID"
              type="text"
              name="searchTerm"
              error={formik.errors.searchTerm}
              touched={formik.touched.searchTerm}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.searchTerm}
              margin="none"
              startAdornment={<RiSearch2Line />}
            />
          </Grid>

          <Grid item xs={12} md={stack ? 12 : 3}>
            <FormSelect
              label="Freezer"
              name="freezerId"
              error={formik.errors.freezerId}
              touched={formik.touched.freezerId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Freezer"
              margin="none"
              options={
                freezerList.map((freezer: Freezer) => ({
                  value: freezer.freezerId,
                  label: `${freezer.name} (${freezer.serialNo})`,
                })) as SelectOption[]
              }
              value={formik.values.freezerId}
            />
          </Grid>

          <Grid item xs={12} md={stack ? 12 : 3}>
            <FormSelect
              label="Shipment Status"
              name="bioBanked"
              error={formik.errors.bioBanked}
              touched={formik.touched.bioBanked}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Shipment Status"
              margin="none"
              options={[
                {
                  value: 'true',
                  label: 'Shipped',
                },
                {
                  value: 'false',
                  label: 'Unshipped',
                },
              ]}
              value={formik.values.bioBanked}
            />
          </Grid>

          <Grid item xs={12} md={stack ? 12 : 2}>
            <Box
              sx={{
                pt: { xs: 0, md: 0 },
                mt: { xs: 0, md: 0 },
              }}
            >
              <Button
                type="submit"
                variant="contained"
                size="large"
                color="primary"
                fullWidth
                endIcon={<RiArrowRightLine />}
                sx={{ height: '55px !important', m: '0 !important' }}
              >
                {buttonLabel}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

export default StorageSearch;
